import type { Recurrence } from '../endpoints/types';

// One recurrence rule inside a node-instance schedule JSON snapshot. Carries
// every user-editable recurrence field (the manual-submit form can set all of
// them) — only the server-assigned identity (`sort_order` /
// `recurrence_definition_recurrence_id`) is excluded, since a snapshot is a
// frozen copy, not a row in the definition table. `resubmit_on_error` is a
// schedule-wide flag on the parent snapshot, not per rule.
export type ScheduleSnapshotRecurrence = Pick<
  Recurrence,
  | 'unit'
  | 'description'
  | 'interval'
  | 'scheduling_window'
  | 'during_scheduling_window'
  | 'preserve_time'
  | 'skip_when_window_closed'
  | 'start_time'
  | 'start_time_zone'
>;

export interface NodeInstanceScheduleSnapshot {
  readonly recurrences: ReadonlyArray<ScheduleSnapshotRecurrence>;
  readonly resubmit_on_error: boolean;
}

export const snapshotRecurrenceToRecurrence = (entry: ScheduleSnapshotRecurrence): Recurrence => ({ ...entry });

/**
 * Build the `node_instances.schedule` snapshot from recurrence rules and a
 * schedule-wide resubmit flag. Returns `null` for an empty rule list so the
 * caller can omit `schedule` entirely.
 */
export const buildScheduleSnapshot = (recurrences: readonly Recurrence[], resubmitOnError = true): NodeInstanceScheduleSnapshot | null => {
  if (!recurrences.length) {
    return null;
  }

  const runtimeRecurrences = recurrences.map(
    (recurrence): ScheduleSnapshotRecurrence => ({
      unit: recurrence.unit,
      ...(recurrence.description != null ? { description: recurrence.description } : {}),
      ...(recurrence.interval != null ? { interval: recurrence.interval } : {}),
      ...(recurrence.scheduling_window ? { scheduling_window: recurrence.scheduling_window } : {}),
      ...(recurrence.during_scheduling_window ? { during_scheduling_window: recurrence.during_scheduling_window } : {}),
      ...(recurrence.preserve_time != null ? { preserve_time: recurrence.preserve_time } : {}),
      ...(recurrence.skip_when_window_closed != null ? { skip_when_window_closed: recurrence.skip_when_window_closed } : {}),
      ...(recurrence.start_time ? { start_time: recurrence.start_time } : {}),
      ...(recurrence.start_time_zone ? { start_time_zone: recurrence.start_time_zone } : {}),
    })
  );

  return {
    recurrences: runtimeRecurrences,
    resubmit_on_error: resubmitOnError !== false,
  };
};
